import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type { OpsRoleBundle } from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Field, FieldDescription, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { grantEmployeeAccess } from "./api";

function RoleBundlePicker({
  onToggle,
  roleBundles,
  selected,
}: {
  onToggle: (roleKey: string) => void;
  roleBundles: OpsRoleBundle[];
  selected: string[];
}) {
  return (
    <fieldset>
      <legend className="text-sm font-semibold">Role bundles</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {roleBundles
          .filter((role) => role.status === "active")
          .map((role) => (
            <label
              className={`flex min-h-10 cursor-pointer items-center gap-2 rounded-sm border px-3 text-sm ${
                selected.includes(role.key) ? "border-primary font-semibold" : ""
              }`}
              key={role.key}
            >
              <input
                checked={selected.includes(role.key)}
                onChange={() => {
                  onToggle(role.key);
                }}
                type="checkbox"
              />
              {role.displayName}
            </label>
          ))}
      </div>
    </fieldset>
  );
}

function GrantForm({
  email,
  error,
  onCancel,
  onEmail,
  onSubmit,
  onToggleRole,
  pending,
  roleBundles,
  roleKeys,
}: {
  email: string;
  error: Error | null;
  onCancel: () => void;
  onEmail: (email: string) => void;
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
  onToggleRole: (roleKey: string) => void;
  pending: boolean;
  roleBundles: OpsRoleBundle[];
  roleKeys: string[];
}) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <Field>
        <FieldLabel htmlFor="grant-email">Sign-in address</FieldLabel>
        <Input
          autoComplete="email"
          id="grant-email"
          onChange={(event) => {
            onEmail(event.target.value);
          }}
          required
          type="email"
          value={email}
        />
        <FieldDescription>The address they already sign in with.</FieldDescription>
      </Field>
      <RoleBundlePicker onToggle={onToggleRole} roleBundles={roleBundles} selected={roleKeys} />
      {error ? <FieldError>{error.message}</FieldError> : null}
      <div className="flex flex-wrap gap-2">
        <Button disabled={pending || roleKeys.length === 0} type="submit">
          {pending ? "Granting…" : "Grant access"}
        </Button>
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Grant operations access without a database write.
 *
 * Adding somebody to operations previously meant inserting an `ops_employees` row by hand, which
 * made the one action nobody could self-serve the one action with no audit trail. The address must
 * already belong to an active platform account: this grants access to a person the platform
 * authenticates, it does not create one.
 */
export function GrantEmployeeAccess({ roleBundles }: { roleBundles: OpsRoleBundle[] }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const grant = useMutation({
    mutationFn: () => grantEmployeeAccess({ email, roleKeys }),
    onSuccess: async () => {
      setEmail("");
      setRoleKeys([]);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["ops", "employees"] });
    },
  });

  function toggleRole(roleKey: string) {
    setRoleKeys((current) =>
      current.includes(roleKey) ? current.filter((key) => key !== roleKey) : [...current, roleKey],
    );
  }

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    grant.mutate();
  }

  if (!open) {
    return (
      <Button
        className="w-fit"
        onClick={() => {
          setOpen(true);
        }}
        type="button"
      >
        <UserPlus /> Grant operations access
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Grant operations access</CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          The person must already have a Delta Adaptive Systems account. This does not create one.
        </p>
      </CardHeader>
      <CardContent>
        <GrantForm
          email={email}
          error={grant.error}
          onCancel={() => {
            setOpen(false);
            grant.reset();
          }}
          onEmail={setEmail}
          onSubmit={submit}
          onToggleRole={toggleRole}
          pending={grant.isPending}
          roleBundles={roleBundles}
          roleKeys={roleKeys}
        />
      </CardContent>
    </Card>
  );
}
